import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useI18n } from '../../../../i18n';
import { AdminCard } from '../components/AdminCard';
import { AdminBadge } from '../components/AdminBadge';
import { AdminToggle } from '../components/AdminToggle';
import { request, adminRequest } from '../../../shared/services/api';
import {
  obtenerConfigCanales,
  actualizarConfigCanales,
  setCredencialCanal,
  clearCredencialCanal,
  type NotifyChannelConfig,
  type CanalConfigurable,
} from '../../../platform/notify/notify-config.service';
import {
  testCanal,
  canalConfigToTestPing,
  type EnvioResult,
  type TestPingLocale,
} from '../../../platform/notify/notify-config-test.service';

// AdminNotificaciones — Tab Notificaciones (PRD-002 0570)
// Bloque 0: preferencias personales del usuario actual (todos los roles)
// Bloque 1: preferencias por evento del tenant (solo admin tenant — TENANT_OWNER/ADMIN/SUPERADMIN)
// Bloque 2: estado de canales de salida (info read-only)
// TENANT_AUDITOR ve todo en modo lectura.

interface NotifPref {
  evento: string;
  email: boolean;
  push: boolean;
  inapp: boolean;
  roles: string[];
}

const COLORS = {
  OSCURO: '#0E243E',
  AZUL1: '#1F3864',
  AZUL2: '#2F5496',
  MEDIO: '#16507D',
  SUAVE: '#D6E4F0',
  ACENTO_SUAVE: '#E8EFF9',
  GRIS: '#F2F2F2',
  GRIS_OSCURO: '#595959',
  VERDE_BG: '#F0FDF4',
  VERDE_TXT: '#15803D',
  AMBER_BG: '#FFFBEB',
  AMBER_TXT: '#92400E',
};

const TH: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: '#fff',
};

const READONLY_BANNER: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  fontSize: 13,
  backgroundColor: '#EFF6FF',
  color: '#1E40AF',
  border: '1px solid #BFDBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 12,
};

interface UserPrefs {
  emailGlobal: boolean;
  tiposEmail: Record<string, boolean>;
}

const NOTIFICATION_TYPES = [
  { key: 'SOLICITUD_PENDIENTE', label: 'Solicitud pendiente de aprobación' },
  { key: 'SOLICITUD_APROBADA', label: 'Solicitud aprobada' },
  { key: 'SOLICITUD_RECHAZADA', label: 'Solicitud rechazada' },
  { key: 'SOLICITUD_DEVUELTA', label: 'Solicitud devuelta' },
  { key: 'SOLICITUD_CANCELADA', label: 'Solicitud cancelada' },
  { key: 'PASO_DELEGADO', label: 'Paso delegado' },
  { key: 'DOCUMENTO_PUBLICADO', label: 'Documento publicado' },
  { key: 'FIRMA_PENDIENTE', label: 'Firma pendiente' },
  { key: 'FIRMA_COMPLETADA', label: 'Firma completada' },
] as const;

// Defaults seed-friendly: si la BD no tiene preferencias aún, mostramos catálogo
// alineado con eventos reales de M16 Requests + M15 Sign + M4 Documentos.
const DEFAULT_NOTIFS: NotifPref[] = [
  {
    evento: 'DOCUMENTO_PUBLICADO',
    email: true,
    push: true,
    inapp: true,
    roles: ['TENANT_OWNER', 'TENANT_ADMIN'],
  },
  {
    evento: 'DOCUMENTO_RECHAZADO',
    email: true,
    push: false,
    inapp: true,
    roles: ['TENANT_OWNER', 'TENANT_ADMIN'],
  },
  {
    evento: 'SOLICITUD_PENDIENTE',
    email: true,
    push: true,
    inapp: true,
    roles: ['TENANT_OWNER', 'TENANT_ADMIN', 'MEMBER'],
  },
  { evento: 'SOLICITUD_APROBADA', email: true, push: true, inapp: true, roles: ['MEMBER'] },
  { evento: 'SOLICITUD_RECHAZADA', email: true, push: true, inapp: true, roles: ['MEMBER'] },
  {
    evento: 'FIRMA_PENDIENTE',
    email: true,
    push: true,
    inapp: true,
    roles: ['TENANT_OWNER', 'TENANT_ADMIN', 'MEMBER'],
  },
  { evento: 'FIRMA_COMPLETADA', email: true, push: false, inapp: true, roles: ['MEMBER'] },
  {
    evento: 'NUEVO_USUARIO',
    email: true,
    push: false,
    inapp: true,
    roles: ['SUPERADMIN_PLATFORM', 'TENANT_OWNER'],
  },
  {
    evento: 'ALERTA_TOKENS_80',
    email: true,
    push: true,
    inapp: true,
    roles: ['SUPERADMIN_PLATFORM', 'TENANT_ADMIN'],
  },
];

// SVG icons inline (regla frontend.md: NUNCA emojis)
const IconEmail = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={COLORS.AZUL1}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const IconBell = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={COLORS.AZUL1}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const IconChat = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={COLORS.AZUL1}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export function AdminNotificaciones() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const role = user?.role ?? 'MEMBER';
  // 0570: solo admin tenant puede mutar preferencias del bloque por evento
  const canEditTenantPrefs =
    role === 'SUPERADMIN_PLATFORM' || role === 'TENANT_OWNER' || role === 'TENANT_ADMIN';

  const [notifs, setNotifs] = useState<NotifPref[]>(DEFAULT_NOTIFS);
  const [userPrefs, setUserPrefs] = useState<UserPrefs>({ emailGlobal: true, tiposEmail: {} });
  const [saving, setSaving] = useState(false);
  // 0658b: config canales (Bloque 2 configurable)
  const [canalesConfig, setCanalesConfig] = useState<NotifyChannelConfig | null>(null);
  const [canalesError, setCanalesError] = useState<string | null>(null);

  useEffect(() => {
    // Bloque 1 (admin tenant): /notifications/preferences (corregido en 0570 — antes era /notifications)
    adminRequest<{ data?: NotifPref[] }>('/notifications/preferences')
      .then((json) => {
        if (Array.isArray(json.data) && json.data.length) setNotifs(json.data);
      })
      .catch(() => {
        /* keep DEFAULT_NOTIFS */
      });

    // Bloque 0 (usuario): preferencias propias
    request<{ data: UserPrefs }>('/notifications/preferencias')
      .then((res) => setUserPrefs(res.data))
      .catch(() => {
        /* keep defaults */
      });

    // Bloque 2 (admin tenant): config canales notif workflow (ADR-027.2)
    if (canEditTenantPrefs) {
      obtenerConfigCanales()
        .then(setCanalesConfig)
        .catch(() => {
          /* primer load — silent */
        });
    }
  }, [canEditTenantPrefs]);

  const savePrefs = useCallback(async (updated: UserPrefs) => {
    setSaving(true);
    try {
      await request('/notifications/preferencias', {
        method: 'PUT',
        body: JSON.stringify(updated),
      });
    } catch {
      /* silent — optimistic UI */
    } finally {
      setSaving(false);
    }
  }, []);

  const toggleEmailGlobal = useCallback(() => {
    const updated = { ...userPrefs, emailGlobal: !userPrefs.emailGlobal };
    setUserPrefs(updated);
    savePrefs(updated);
  }, [userPrefs, savePrefs]);

  const toggleTipoEmail = useCallback(
    (tipo: string) => {
      const current = userPrefs.tiposEmail[tipo] !== false;
      const updated = {
        ...userPrefs,
        tiposEmail: { ...userPrefs.tiposEmail, [tipo]: !current },
      };
      setUserPrefs(updated);
      savePrefs(updated);
    },
    [userPrefs, savePrefs],
  );

  // 0570: persiste cada toggle del bloque admin via PUT /notifications/preferences/:evento
  const toggleAdminPref = async (idx: number, ch: 'email' | 'push' | 'inapp') => {
    if (!canEditTenantPrefs) return;
    const pref = notifs[idx];
    if (!pref) return;
    const nuevo = { ...pref, [ch]: !pref[ch] };

    setNotifs((prev) => prev.map((n, i) => (i === idx ? nuevo : n)));
    setSaving(true);
    try {
      await adminRequest(`/notifications/preferences/${pref.evento}`, {
        method: 'PUT',
        body: JSON.stringify({
          email: nuevo.email,
          push: nuevo.push,
          inapp: nuevo.inapp,
          roles: nuevo.roles,
        }),
      });
    } catch {
      // Revert optimista
      setNotifs((prev) => prev.map((n, i) => (i === idx ? pref : n)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 24 }}>
      {!canEditTenantPrefs && (
        <div style={READONLY_BANNER}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <span>
            Las preferencias por evento del tenant son de solo lectura para tu rol. Tus preferencias
            personales (bloque siguiente) sí son editables.
          </span>
        </div>
      )}

      {/* Bloque 0: Preferencias personales — siempre editables por el dueño */}
      <AdminCard title="Mis preferencias de email" accent={COLORS.AZUL1}>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          <AdminToggle checked={userPrefs.emailGlobal} onChange={toggleEmailGlobal} />
          <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.OSCURO }}>
            Recibir emails de notificación
          </span>
          {saving && (
            <span style={{ fontSize: 11, color: COLORS.GRIS_OSCURO, fontStyle: 'italic' }}>
              Guardando...
            </span>
          )}
        </div>
        {userPrefs.emailGlobal && (
          <div style={{ borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.OSCURO }}>
                  <th style={{ ...TH, textAlign: 'left' }}>Tipo de notificación</th>
                  <th style={{ ...TH, textAlign: 'center', width: 80 }}>In-App</th>
                  <th style={{ ...TH, textAlign: 'center', width: 80 }}>Email</th>
                </tr>
              </thead>
              <tbody>
                {NOTIFICATION_TYPES.map((nt, idx) => (
                  <tr
                    key={nt.key}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: idx % 2 === 1 ? COLORS.SUAVE : undefined,
                    }}
                  >
                    <td
                      style={{
                        padding: '10px 14px',
                        fontWeight: 600,
                        fontSize: 12,
                        color: COLORS.OSCURO,
                      }}
                    >
                      {nt.label}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <AdminBadge bg={COLORS.VERDE_BG} color={COLORS.VERDE_TXT}>
                        Siempre
                      </AdminBadge>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <AdminToggle
                        checked={userPrefs.tiposEmail[nt.key] !== false}
                        onChange={() => toggleTipoEmail(nt.key)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      {/* Bloque 1: Preferencias por evento — solo admin tenant */}
      <AdminCard title="Preferencias de notificación por evento del tenant" accent={COLORS.AZUL2}>
        <div style={{ borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.OSCURO }}>
                <th style={{ ...TH, textAlign: 'left' }}>Evento</th>
                <th style={{ ...TH, textAlign: 'center', width: 80 }}>Email</th>
                <th style={{ ...TH, textAlign: 'center', width: 80 }}>Push</th>
                <th style={{ ...TH, textAlign: 'center', width: 80 }}>In-App</th>
                <th style={{ ...TH, textAlign: 'left' }}>Roles que reciben</th>
              </tr>
            </thead>
            <tbody>
              {notifs.map((n, idx) => (
                <tr
                  key={n.evento}
                  style={{
                    borderBottom: '1px solid #f3f4f6',
                    backgroundColor: idx % 2 === 1 ? COLORS.SUAVE : undefined,
                  }}
                >
                  <td
                    style={{
                      padding: '10px 14px',
                      fontWeight: 600,
                      fontSize: 12,
                      color: COLORS.OSCURO,
                    }}
                  >
                    {n.evento}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <AdminToggle
                      checked={n.email}
                      disabled={!canEditTenantPrefs}
                      onChange={() => toggleAdminPref(idx, 'email')}
                    />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <AdminToggle
                      checked={n.push}
                      disabled={!canEditTenantPrefs}
                      onChange={() => toggleAdminPref(idx, 'push')}
                    />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <AdminToggle
                      checked={n.inapp}
                      disabled={!canEditTenantPrefs}
                      onChange={() => toggleAdminPref(idx, 'inapp')}
                    />
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {n.roles.map((r) => (
                        <AdminBadge key={r} bg={COLORS.ACENTO_SUAVE} color={COLORS.AZUL2}>
                          {r}
                        </AdminBadge>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      {/* Bloque 2: Canales de salida (configurable) — ADR-027.2, prompt 0658b */}
      <AdminCard title={t.admin.notificaciones.canales.titulo} accent={COLORS.MEDIO}>
        <div style={{ fontSize: 12, color: COLORS.GRIS_OSCURO, marginBottom: 12 }}>
          {t.admin.notificaciones.canales.descripcion}
        </div>
        {!canEditTenantPrefs ? (
          <div style={{ fontSize: 12, color: COLORS.GRIS_OSCURO, fontStyle: 'italic' }}>
            {/* admin tenant only — usuarios sin permiso ven mensaje read-only del bloque 1 */}
          </div>
        ) : !canalesConfig ? (
          <div style={{ fontSize: 12, color: COLORS.GRIS_OSCURO }}>...</div>
        ) : (
          <>
            {/* Toggle global modo sandbox */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 14,
                padding: 10,
                backgroundColor: COLORS.AMBER_BG,
                borderRadius: 6,
              }}
            >
              <AdminToggle
                checked={canalesConfig.modoSandbox}
                onChange={async () => {
                  setCanalesError(null);
                  try {
                    const updated = await actualizarConfigCanales({
                      modoSandbox: !canalesConfig.modoSandbox,
                    });
                    setCanalesConfig(updated);
                  } catch (e) {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: COLORS.AMBER_TXT }}>
                  {t.admin.notificaciones.canales.modoSandbox}
                </div>
                <div style={{ fontSize: 11, color: COLORS.GRIS_OSCURO, marginTop: 2 }}>
                  {t.admin.notificaciones.canales.modoSandboxDescripcion}
                </div>
              </div>
            </div>

            {/* 3 cards Mail / Teams / Slack */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <ChannelConfigCard
                canal="mail"
                icon={<IconEmail />}
                titulo={t.admin.notificaciones.canales.mailTitulo}
                descripcion={t.admin.notificaciones.canales.mailDescripcion}
                habilitarLabel={t.admin.notificaciones.canales.mailHabilitar}
                credencialLabel={t.admin.notificaciones.canales.mailApiKey}
                habilitado={canalesConfig.mailHabilitado}
                tieneCredencial={canalesConfig.tieneCredencialMail}
                extraField={{
                  label: t.admin.notificaciones.canales.mailFromEmail,
                  value: canalesConfig.mailFromEmail ?? '',
                  onSave: async (v) => {
                    setCanalesError(null);
                    try {
                      const updated = await actualizarConfigCanales({ mailFromEmail: v || null });
                      setCanalesConfig(updated);
                    } catch {
                      setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                    }
                  },
                }}
                onToggleHabilitar={async (next) => {
                  setCanalesError(null);
                  try {
                    const updated = await actualizarConfigCanales({ mailHabilitado: next });
                    setCanalesConfig(updated);
                  } catch {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
                onSaveCredencial={async (value) => {
                  setCanalesError(null);
                  try {
                    const updated = await setCredencialCanal('mail', value);
                    setCanalesConfig(updated);
                  } catch {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
                onClearCredencial={async () => {
                  setCanalesError(null);
                  try {
                    const updated = await clearCredencialCanal('mail');
                    setCanalesConfig(updated);
                  } catch {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
                onTestCanal={async () =>
                  testCanal(canalConfigToTestPing.mail, lang as TestPingLocale)
                }
                t={t.admin.notificaciones.canales}
              />

              <ChannelConfigCard
                canal="teams"
                icon={<IconChat />}
                titulo={t.admin.notificaciones.canales.teamsTitulo}
                descripcion=""
                habilitarLabel={t.admin.notificaciones.canales.teamsHabilitar}
                credencialLabel={t.admin.notificaciones.canales.teamsWebhook}
                habilitado={canalesConfig.teamsHabilitado}
                tieneCredencial={canalesConfig.tieneCredencialTeams}
                onToggleHabilitar={async (next) => {
                  setCanalesError(null);
                  try {
                    const updated = await actualizarConfigCanales({ teamsHabilitado: next });
                    setCanalesConfig(updated);
                  } catch {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
                onSaveCredencial={async (value) => {
                  setCanalesError(null);
                  try {
                    const updated = await setCredencialCanal('teams', value);
                    setCanalesConfig(updated);
                  } catch {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
                onClearCredencial={async () => {
                  setCanalesError(null);
                  try {
                    const updated = await clearCredencialCanal('teams');
                    setCanalesConfig(updated);
                  } catch {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
                onTestCanal={async () =>
                  testCanal(canalConfigToTestPing.teams, lang as TestPingLocale)
                }
                t={t.admin.notificaciones.canales}
              />

              <ChannelConfigCard
                canal="slack"
                icon={<IconBell />}
                titulo={t.admin.notificaciones.canales.slackTitulo}
                descripcion=""
                habilitarLabel={t.admin.notificaciones.canales.slackHabilitar}
                credencialLabel={t.admin.notificaciones.canales.slackWebhook}
                habilitado={canalesConfig.slackHabilitado}
                tieneCredencial={canalesConfig.tieneCredencialSlack}
                onToggleHabilitar={async (next) => {
                  setCanalesError(null);
                  try {
                    const updated = await actualizarConfigCanales({ slackHabilitado: next });
                    setCanalesConfig(updated);
                  } catch {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
                onSaveCredencial={async (value) => {
                  setCanalesError(null);
                  try {
                    const updated = await setCredencialCanal('slack', value);
                    setCanalesConfig(updated);
                  } catch {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
                onClearCredencial={async () => {
                  setCanalesError(null);
                  try {
                    const updated = await clearCredencialCanal('slack');
                    setCanalesConfig(updated);
                  } catch {
                    setCanalesError(t.admin.notificaciones.canales.errorGuardar);
                  }
                }}
                onTestCanal={async () =>
                  testCanal(canalConfigToTestPing.slack, lang as TestPingLocale)
                }
                t={t.admin.notificaciones.canales}
              />
            </div>

            {canalesError && (
              <div
                style={{
                  marginTop: 12,
                  padding: 10,
                  borderRadius: 6,
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#991B1B',
                  fontSize: 12,
                }}
              >
                {canalesError}
              </div>
            )}
          </>
        )}
      </AdminCard>
    </div>
  );
}

interface ChannelConfigCardProps {
  canal: CanalConfigurable;
  icon: React.ReactNode;
  titulo: string;
  descripcion: string;
  habilitarLabel: string;
  credencialLabel: string;
  habilitado: boolean;
  tieneCredencial: boolean;
  extraField?: {
    label: string;
    value: string;
    onSave: (newValue: string) => Promise<void>;
  };
  onToggleHabilitar: (next: boolean) => Promise<void>;
  onSaveCredencial: (value: string) => Promise<void>;
  onClearCredencial: () => Promise<void>;
  // ADR-027.3 (0658c): boton "Probar canal" + audit trail. Opcional para retro-compat.
  onTestCanal?: () => Promise<EnvioResult>;
  t: {
    cambiar: string;
    limpiar: string;
    credencialGuardada: string;
    credencialAusente: string;
    nuevoValor: string;
    // ADR-027.3 (0658c): claves del bloque "Probar canal". Opcional para retro-compat.
    test?: {
      botonProbar: string;
      enviando: string;
      exito: string;
      error: string;
      mensajeEnviadoEn: string;
      messageId: string;
      reasonNoProvider: string;
      reasonProviderError: string;
      reasonSandboxMode: string;
      confirmarTest: string;
    };
  };
}

function ChannelConfigCard({
  canal,
  icon,
  titulo,
  descripcion,
  habilitarLabel,
  credencialLabel,
  habilitado,
  tieneCredencial,
  extraField,
  onToggleHabilitar,
  onSaveCredencial,
  onClearCredencial,
  onTestCanal,
  t,
}: ChannelConfigCardProps) {
  const { t: tRoot } = useI18n();
  const [editandoCred, setEditandoCred] = useState(false);
  const [credValue, setCredValue] = useState('');
  const [editandoExtra, setEditandoExtra] = useState(false);
  const [extraValue, setExtraValue] = useState(extraField?.value ?? '');
  // ADR-027.3 (0658c): estado del boton "Probar canal" + resultado inline
  const [enviandoTest, setEnviandoTest] = useState(false);
  const [testResultado, setTestResultado] = useState<EnvioResult | null>(null);
  const [testTimestamp, setTestTimestamp] = useState<string | null>(null);

  const tTest = t.test;
  const handleTest = async () => {
    if (!onTestCanal || !tTest) return;
    if (!window.confirm(tTest.confirmarTest)) return;
    setEnviandoTest(true);
    setTestResultado(null);
    try {
      const result = await onTestCanal();
      setTestResultado(result);
      setTestTimestamp(new Date().toLocaleTimeString());
    } catch (err) {
      setTestResultado({
        sent: false,
        reason: 'PROVIDER_ERROR',
        detail: (err as Error).message,
      });
      setTestTimestamp(new Date().toLocaleTimeString());
    } finally {
      setEnviandoTest(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: COLORS.GRIS,
        borderRadius: 8,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
      data-testid={`channel-card-${canal}`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: COLORS.AZUL1 }}>{titulo}</div>
          {descripcion && (
            <div style={{ fontSize: 10, color: COLORS.GRIS_OSCURO }}>{descripcion}</div>
          )}
        </div>
      </div>

      {/* Toggle habilitar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <AdminToggle checked={habilitado} onChange={() => onToggleHabilitar(!habilitado)} />
        <span style={{ fontSize: 11, color: COLORS.OSCURO }}>{habilitarLabel}</span>
      </div>

      {/* Credencial — masked si guardada, input si editando */}
      <div>
        <div style={{ fontSize: 11, color: COLORS.GRIS_OSCURO, marginBottom: 4 }}>
          {credencialLabel}
        </div>
        {editandoCred ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              type="password"
              autoComplete="off"
              placeholder={t.nuevoValor}
              value={credValue}
              onChange={(e) => setCredValue(e.target.value)}
              style={{
                padding: '6px 8px',
                borderRadius: 4,
                border: `1px solid ${COLORS.MEDIO}`,
                fontSize: 12,
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={async () => {
                  if (credValue.trim()) {
                    await onSaveCredencial(credValue.trim());
                    setCredValue('');
                    setEditandoCred(false);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  borderRadius: 4,
                  border: 'none',
                  backgroundColor: COLORS.AZUL1,
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {tRoot.common.guardar}
              </button>
              <button
                onClick={() => {
                  setCredValue('');
                  setEditandoCred(false);
                }}
                aria-label="cancel"
                style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  border: `1px solid ${COLORS.GRIS_OSCURO}`,
                  backgroundColor: '#fff',
                  fontSize: 11,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={COLORS.GRIS_OSCURO}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {tieneCredencial ? (
              <AdminBadge bg={COLORS.VERDE_BG} color={COLORS.VERDE_TXT}>
                {t.credencialGuardada}
              </AdminBadge>
            ) : (
              <AdminBadge bg={COLORS.AMBER_BG} color={COLORS.AMBER_TXT}>
                {t.credencialAusente}
              </AdminBadge>
            )}
            <button
              onClick={() => setEditandoCred(true)}
              style={{
                padding: '3px 8px',
                borderRadius: 4,
                border: `1px solid ${COLORS.AZUL1}`,
                backgroundColor: '#fff',
                color: COLORS.AZUL1,
                fontSize: 10,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t.cambiar}
            </button>
            {tieneCredencial && (
              <button
                onClick={onClearCredencial}
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: `1px solid #DC2626`,
                  backgroundColor: '#fff',
                  color: '#DC2626',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t.limpiar}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ADR-027.3 (0658c): Boton "Probar canal" + resultado inline */}
      {onTestCanal && tTest && tieneCredencial && habilitado && (
        <div>
          <button
            onClick={handleTest}
            disabled={enviandoTest}
            data-testid={`btn-test-${canal}`}
            style={{
              padding: '4px 10px',
              borderRadius: 4,
              border: `1px solid ${COLORS.MEDIO}`,
              backgroundColor: COLORS.ACENTO_SUAVE,
              color: COLORS.AZUL1,
              fontSize: 11,
              fontWeight: 600,
              cursor: enviandoTest ? 'wait' : 'pointer',
              opacity: enviandoTest ? 0.6 : 1,
            }}
          >
            {enviandoTest ? tTest.enviando : tTest.botonProbar}
          </button>
          {testResultado && testTimestamp && (
            <div
              data-testid={`test-result-${canal}`}
              style={{
                marginTop: 6,
                padding: '6px 8px',
                borderRadius: 4,
                fontSize: 10,
                backgroundColor: testResultado.sent ? COLORS.VERDE_BG : '#FEF2F2',
                color: testResultado.sent ? COLORS.VERDE_TXT : '#991B1B',
                border: `1px solid ${testResultado.sent ? '#86EFAC' : '#FECACA'}`,
              }}
            >
              {testResultado.sent ? (
                <>
                  <div style={{ fontWeight: 600 }}>{tTest.exito}</div>
                  <div style={{ opacity: 0.85, marginTop: 2 }}>
                    {tTest.mensajeEnviadoEn.replace('{timestamp}', testTimestamp)}
                  </div>
                  {testResultado.messageIds[0] && (
                    <div style={{ opacity: 0.75, fontFamily: 'monospace', marginTop: 2 }}>
                      {tTest.messageId.replace(
                        '{id}',
                        testResultado.messageIds[0].length > 22
                          ? testResultado.messageIds[0].slice(0, 18) + '...'
                          : testResultado.messageIds[0],
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontWeight: 600 }}>
                  {tTest.error.replace(
                    '{detalle}',
                    testResultado.reason === 'NO_PROVIDER_CONFIGURED' ||
                      testResultado.reason === 'NO_DESTINATARIOS'
                      ? tTest.reasonNoProvider
                      : testResultado.reason === 'SANDBOX_MODE'
                        ? tTest.reasonSandboxMode
                        : tTest.reasonProviderError,
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Campo extra (solo mail: fromEmail) */}
      {extraField && (
        <div>
          <div style={{ fontSize: 11, color: COLORS.GRIS_OSCURO, marginBottom: 4 }}>
            {extraField.label}
          </div>
          {editandoExtra ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="email"
                value={extraValue}
                onChange={(e) => setExtraValue(e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  borderRadius: 4,
                  border: `1px solid ${COLORS.MEDIO}`,
                  fontSize: 12,
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={async () => {
                  await extraField.onSave(extraValue.trim());
                  setEditandoExtra(false);
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: 4,
                  border: 'none',
                  backgroundColor: COLORS.AZUL1,
                  color: '#fff',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                {tRoot.common.guardar}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: COLORS.OSCURO }}>{extraField.value || '—'}</span>
              <button
                onClick={() => setEditandoExtra(true)}
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: `1px solid ${COLORS.AZUL1}`,
                  backgroundColor: '#fff',
                  color: COLORS.AZUL1,
                  fontSize: 10,
                  cursor: 'pointer',
                }}
              >
                {t.cambiar}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
